import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  type OutreachCampaign,
  type OutreachEvidenceOption,
  type OutreachMessageTone,
  type OutreachProspect,
  type OutreachProspectState,
  type OutreachQueueItem,
  type OutreachStep,
  type OutreachStepKind,
} from '@/api/client'
import { cn } from '@/lib/utils'
import { Button, Field, Select, StatusPill, TonePill, Textarea } from '@/ui'
import {
  CampaignHeader,
  ErrorNotice,
  Hollow,
  LoadingCard,
  PlainHeader,
  errorMessage,
  isAbortError,
  errorCodeOf,
  isValidRouteID,
} from './shared'
import { ENDING_REASON, TerminalConfirm, type EndingKind } from './TerminalConfirm'

const kinds: OutreachStepKind[] = ['connection', 'praise', 'cta']
const KIND_LABEL: Record<OutreachStepKind, string> = { connection: '연결 요청', praise: '칭찬', cta: '제안' }
const TONE_LABEL: Record<OutreachMessageTone, string> = { warm: '따뜻하게', concise: '간결하게', curious: '궁금해하며' }
const tones: OutreachMessageTone[] = ['warm', 'concise', 'curious']
const generationStates: Record<OutreachStepKind, OutreachProspectState[]> = {
  connection: ['qualified', 'connection_ready'],
  praise: ['connected', 'praise_ready'],
  cta: ['replied', 'cta_ready'],
}
const readyStates: Record<OutreachStepKind, OutreachProspectState> = {
  connection: 'connection_ready',
  praise: 'praise_ready',
  cta: 'cta_ready',
}
const sentStates: Record<OutreachStepKind, OutreachProspectState> = {
  connection: 'connection_sent',
  praise: 'praise_sent',
  cta: 'cta_sent',
}
const terminalStates: OutreachProspectState[] = [
  'closed',
  'skipped',
  'do_not_contact',
  'invalid_profile',
]

export default function OutreachQueue() {
  const campaignId = Number(useParams().campaignId)
  const validID = isValidRouteID(campaignId)
  const [campaign, setCampaign] = useState<OutreachCampaign | null>(null)
  const [items, setItems] = useState<OutreachQueueItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(validID)
  const [reloadKey, setReloadKey] = useState(0)
  // The campaign whose queue is on screen: a reload for the same one (after an
  // action on a card) refreshes behind the cards instead of remounting them.
  const shownCampaignRef = useRef<number | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const sameCampaign = shownCampaignRef.current === campaignId
    if (!sameCampaign) {
      shownCampaignRef.current = null
      setCampaign(null)
      setItems([])
    }
    setError(null)
    if (!validID) {
      setLoading(false)
      return () => controller.abort()
    }
    if (!sameCampaign) setLoading(true)
    void Promise.all([
      api.outreachCampaign(campaignId, controller.signal),
      api.outreachQueue(campaignId, controller.signal),
    ])
      .then(([campaignData, queueData]) => {
        if (controller.signal.aborted) return
        const nextItems = queueData.items ?? []
        if (
          campaignData.id !== campaignId ||
          nextItems.some((item) => item.prospect.campaign_id !== campaignId)
        ) {
          throw new Error('Outreach response did not match the current campaign route')
        }
        shownCampaignRef.current = campaignId
        setCampaign(campaignData)
        setItems(nextItems)
      })
      .catch((err: unknown) => {
        if (!isAbortError(err)) setError(errorMessage(err))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [campaignId, reloadKey, validID])

  if (!validID) {
    return (
      <div className="space-y-4">
        <PlainHeader title="작업 큐" description="캠페인 주소의 ID가 올바르지 않다." />
        <ErrorNotice message="올바르지 않은 아웃리치 캠페인 ID다." />
      </div>
    )
  }

  const currentCampaign = campaign?.id === campaignId ? campaign : null
  const currentItems = currentCampaign
    ? items.filter((item) => item.prospect.campaign_id === campaignId)
    : []
  const routeLoading = loading || (!error && !currentCampaign)

  return (
    <div className="space-y-4">
      <CampaignHeader
        campaignId={campaignId}
        title={currentCampaign ? `${currentCampaign.name} 작업 큐` : '아웃리치 작업 큐'}
        description="메시지를 한 건씩 준비한다. 승인은 표시일 뿐이다. 복사, LinkedIn 열기, 보냄 표시는 직접 누른다."
      />
      <ErrorNotice message={error} />
      {routeLoading ? (
        <LoadingCard>작업 큐를 불러오는 중…</LoadingCard>
      ) : error ? null : currentItems.length === 0 ? (
        <Hollow>아직 메이커가 없다. 발굴에서 메이커를 찾고 적격 처리하면 여기에 올라온다.</Hollow>
      ) : (
        <div className="space-y-4">
          {currentItems.map((item) => (
            <QueueRow
              key={item.prospect.id}
              item={item}
              actionsEnabled={
                currentCampaign?.id === campaignId &&
                currentCampaign.status === 'active' &&
                item.prospect.campaign_id === campaignId &&
                item.contact_allowed
              }
              onChanged={() => setReloadKey((value) => value + 1)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function QueueRow({
  item,
  actionsEnabled,
  onChanged,
}: {
  item: OutreachQueueItem
  actionsEnabled: boolean
  onChanged: () => void
}) {
  const [safetyBusy, setSafetyBusy] = useState(false)
  const [safetyError, setSafetyError] = useState<string | null>(null)
  const [ending, setEnding] = useState<EndingKind | null>(null)
  const terminal = terminalStates.includes(item.prospect.state)
  const safetyAction = async (action: () => Promise<unknown>) => {
    setSafetyBusy(true)
    setSafetyError(null)
    try {
      await action()
      onChanged()
    } catch (err) {
      setSafetyError(errorMessage(err))
    } finally {
      setSafetyBusy(false)
    }
  }
  return (
    <article className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <Link
            to={`/outreach/${item.prospect.campaign_id}/prospects/${item.prospect.id}`}
            className="text-section font-semibold hover:underline"
          >
            {item.prospect.maker_name} · {item.prospect.product_name}
          </Link>
          <p className="mt-0.5 text-meta text-muted-foreground">
            점수 {item.prospect.score} · {item.prospect.linkedin_verified_at ? 'LinkedIn 확인됨' : 'LinkedIn 확인 필요'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill domain="prospect" status={item.prospect.state} />
          {item.duplicate_confirmation_required && (
            <Button
              size="sm"
              loading={safetyBusy}
              onClick={() => void safetyAction(() => api.reconfirmOutreachDuplicate(item.prospect.id))}
            >
              중복 다시 확인
            </Button>
          )}
          <Button
            size="sm"
            variant="dangerOutline"
            loading={safetyBusy}
            disabled={terminal}
            onClick={() => setEnding('do_not_contact')}
          >
            연락 금지
          </Button>
        </div>
      </div>
      {!actionsEnabled && !terminal && (
        <div className="border-b border-border px-4 py-2 text-meta text-status-publishing">
          {item.safety_block_reason || '캠페인이 멈춰 있는 동안은 연락 동작을 쓸 수 없다.'}
        </div>
      )}
      <TerminalConfirm
        kind={ending}
        maker={item.prospect.maker_name}
        onCancel={() => setEnding(null)}
        onConfirm={async (kind) => {
          setEnding(null)
          if (kind === 'do_not_contact') {
            await safetyAction(() =>
              api.transitionOutreachProspect(item.prospect.id, 'do_not_contact', ENDING_REASON.do_not_contact),
            )
          }
        }}
      />
      {safetyError && (
        <div className="px-4 pt-3">
          <ErrorNotice message={safetyError} />
        </div>
      )}
      <div className="grid gap-3 p-4 lg:grid-cols-3">
        {kinds.map((kind) => (
          <StepColumn
            key={kind}
            kind={kind}
            prospect={item.prospect}
            step={item.steps.find((candidate) => candidate.kind === kind)}
            evidenceOptions={item.evidence_options}
            actionsEnabled={actionsEnabled}
            onChanged={onChanged}
          />
        ))}
      </div>
    </article>
  )
}

export function StepColumn({
  kind,
  prospect,
  step,
  evidenceOptions,
  actionsEnabled = true,
  onChanged,
}: {
  kind: OutreachStepKind
  prospect: OutreachProspect
  step?: OutreachStep
  evidenceOptions: OutreachEvidenceOption[]
  actionsEnabled?: boolean
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [copied, setCopied] = useState(false)
  const [skipAsk, setSkipAsk] = useState(false)
  const terminal = terminalStates.includes(prospect.state)
  const canGenerate = actionsEnabled && !terminal && generationStates[kind].includes(prospect.state)
  const canApprove =
    actionsEnabled && !terminal && step?.status === 'draft' && prospect.state === readyStates[kind]
  const canCopy = actionsEnabled && !terminal && step?.status === 'approved' && step.copy_eligible
  const canOpen = actionsEnabled && !terminal && step?.status === 'approved' && step.open_eligible
  const canMarkSent =
    actionsEnabled &&
    !terminal &&
    step?.status === 'approved' &&
    prospect.state === readyStates[kind]
  const currentStage =
    generationStates[kind].includes(prospect.state) || prospect.state === sentStates[kind]

  const [errorCode, setErrorCode] = useState('')
  const fail = (err: unknown) => {
    setError(errorMessage(err))
    setErrorCode(errorCodeOf(err))
  }
  const run = async (name: string, action: () => Promise<unknown>) => {
    if (!actionsEnabled) return
    setBusy(name)
    setError(null)
    setErrorCode('')
    try {
      await action()
      onChanged()
    } catch (err) {
      fail(err)
    } finally {
      setBusy(null)
    }
  }

  const copy = async () => {
    if (!step || !canCopy) return
    try {
      const copyable = await api.copyOutreachStep(step.id)
      await navigator.clipboard.writeText(copyable.body_text)
      setError(null)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch (err) {
      fail(err)
    }
  }

  return (
    <section
      aria-label={`${KIND_LABEL[kind]} 단계`}
      className="flex min-w-0 flex-col rounded-lg border border-border bg-background p-3"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-body font-semibold">{KIND_LABEL[kind]}</h3>
        {step ? <StatusPill domain="step" status={step.status} /> : <TonePill tone="neutral" label="준비 전" />}
      </div>
      <div className="min-h-24 flex-1 rounded-md bg-muted/50 p-3 text-table leading-5 text-muted-foreground">
        {step ? (
          <p className="line-clamp-5 whitespace-pre-wrap">{step.body_text}</p>
        ) : (
          <p>앞 단계를 직접 마친 뒤에 이 메시지를 준비할 수 있다.</p>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Button
          size="sm"
          disabled={
            !step ||
            step.status === 'sent' ||
            prospect.state !== readyStates[kind] ||
            terminal ||
            !actionsEnabled
          }
          onClick={() => setEditing((value) => !value)}
        >
          수정
        </Button>
        <Button
          size="sm"
          loading={busy === 'regenerate'}
          disabled={!canGenerate}
          onClick={() => void run('regenerate', () => api.generateOutreachStep(prospect.id, kind))}
        >
          다시 만들기
        </Button>
        <Button
          size="sm"
          variant="primary"
          loading={busy === 'approve'}
          disabled={!canApprove}
          onClick={() => step && void run('approve', () => api.approveOutreachStep(step.id))}
        >
          승인
        </Button>
        <Button size="sm" variant="ghost" disabled={!canCopy} onClick={() => void copy()}>
          {copied ? '복사함' : '복사'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canOpen || !prospect.linkedin_url}
          onClick={() =>
            prospect.linkedin_url &&
            window.open(prospect.linkedin_url, '_blank', 'noopener,noreferrer')
          }
        >
          LinkedIn 열기
        </Button>
        <Button
          size="sm"
          variant="primary"
          loading={busy === 'sent'}
          disabled={!canMarkSent}
          onClick={() =>
            void run('sent', () => api.transitionOutreachProspect(prospect.id, sentStates[kind]))
          }
        >
          보냄 표시
        </Button>
        <Button
          size="sm"
          variant="dangerOutline"
          loading={busy === 'skip'}
          disabled={!actionsEnabled || terminal || !currentStage}
          title="이 단계만 넘기는 것이 아니라 메이커를 끝낸다"
          onClick={() => setSkipAsk(true)}
        >
          메이커 제외
        </Button>
      </div>
      <TerminalConfirm
        kind={skipAsk ? 'skipped' : null}
        maker={prospect.maker_name}
        onCancel={() => setSkipAsk(false)}
        onConfirm={async () => {
          setSkipAsk(false)
          await run('skip', () =>
            api.transitionOutreachProspect(prospect.id, 'skipped', `${kind} skipped manually`),
          )
        }}
      />
      <div className={cn(error && 'mt-3')}>
        <ErrorNotice message={error} />
        {errorCode === 'generator_not_configured' && (
          <Link to="/settings?tab=llm.chat" className="mt-1 inline-block text-table text-foreground underline">
            설정 열기
          </Link>
        )}
      </div>
      {editing && step && (
        <StructuredEdit
          step={step}
          evidenceOptions={evidenceOptions}
          onClose={() => setEditing(false)}
          onChanged={() => {
            setError(null)
            setErrorCode('')
            onChanged()
          }}
        />
      )}
    </section>
  )
}

export function StructuredEdit({
  step,
  evidenceOptions,
  onClose,
  onChanged,
}: {
  step: OutreachStep
  evidenceOptions: OutreachEvidenceOption[]
  onClose: () => void
  onChanged: () => void
}) {
  const [evidenceId, setEvidenceId] = useState(step.evidence_ids[0] ?? evidenceOptions[0]?.id ?? '')
  const [tone, setTone] = useState<OutreachMessageTone>(step.tone)
  const [preview, setPreview] = useState('')
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectionKey = `${evidenceId}:${tone}`

  useEffect(() => {
    const controller = new AbortController()
    setPreview('')
    setPreviewKey(null)
    setError(null)
    if (!evidenceId) return () => controller.abort()
    setPreviewing(true)
    void api
      .previewOutreachStep(step.id, evidenceId, tone, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        if (result.evidence_id !== evidenceId || result.tone !== tone) {
          throw new Error('Preview did not match the current evidence and tone selection')
        }
        setPreview(result.body_text)
        setPreviewKey(selectionKey)
      })
      .catch((err: unknown) => {
        if (!isAbortError(err)) setError(errorMessage(err))
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewing(false)
      })
    return () => controller.abort()
  }, [evidenceId, selectionKey, step.id, tone])

  const apply = async () => {
    if (previewKey !== selectionKey) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.updateOutreachStep(step.id, evidenceId, tone, preview)
      if (updated.body_text !== preview)
        throw new Error('Saved message did not match the approved preview')
      onChanged()
      onClose()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 space-y-4 rounded-lg border border-border bg-card p-3">
      <div>
        <h4 className="text-body font-semibold">근거와 말투 고르기</h4>
        <p className="mt-1 text-meta text-muted-foreground">
          서버가 허용한 근거 하나와 정해진 말투 중에서 고른다. 미리보기는 서버가 그대로 만든 문장이라 직접 고칠 수 없다.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="근거">
          <Select value={evidenceId} onChange={(event) => setEvidenceId(event.target.value)}>
            {evidenceOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}: {option.value}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="말투">
          <Select value={tone} onChange={(event) => setTone(event.target.value as OutreachMessageTone)}>
            {tones.map((value) => (
              <option key={value} value={value}>
                {TONE_LABEL[value]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="확정 문장 미리보기">
        <Textarea
          readOnly
          aria-readonly="true"
          rows={6}
          className="resize-none bg-muted/50"
          value={preview}
          placeholder={previewing ? '서버에서 문장을 만드는 중…' : '미리보기를 가져오지 못했다'}
        />
      </Field>
      <ErrorNotice message={error} />
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>닫기</Button>
        <Button
          variant="primary"
          loading={busy}
          disabled={previewing || previewKey !== selectionKey}
          onClick={() => void apply()}
        >
          선택 적용
        </Button>
      </div>
    </div>
  )
}
